import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import MenuSheet from "@/components/MenuSheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  Settings as SettingsIcon,
  Bell,
  BellOff,
  Palette,
  User,
  Mail,
  ArrowRight,
  Trash2,
  AlertTriangle,
  ShieldOff,
  Sunrise,
  Clock,
} from "lucide-react";
import { requestNotificationPermission, notifySupported } from "@/lib/notifications";
import api from "@/lib/api";
import { setAuthToken } from "@/lib/api";
import { toast } from "sonner";

export default function SettingsPage() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [notifPerm, setNotifPerm] = useState(
    notifySupported() ? Notification.permission : "unsupported"
  );
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [profileName, setProfileName] = useState(user?.name || "");
  const [savingName, setSavingName] = useState(false);

  // Daily digest preferences — local mirror so changes feel instant.
  const browserTz =
    Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const [digestEnabled, setDigestEnabled] = useState(
    Boolean(user?.digest_enabled)
  );
  const [digestTime, setDigestTime] = useState(user?.digest_time || "08:00");
  const [digestTz, setDigestTz] = useState(user?.digest_timezone || browserTz);
  const [savingDigest, setSavingDigest] = useState(false);

  const saveDigest = async (patch) => {
    setSavingDigest(true);
    try {
      const { data } = await api.patch("/settings", patch);
      setUser({ ...user, ...data });
      toast.success("Digest preferences saved");
    } catch (e) {
      toast.error("Could not save", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setSavingDigest(false);
    }
  };

  const toggleDigest = (next) => {
    setDigestEnabled(next);
    saveDigest({
      digest_enabled: next,
      digest_time: digestTime,
      digest_timezone: digestTz,
    });
  };

  const saveProfileName = async () => {
    const next = (profileName || "").trim();
    if (!next) {
      toast.error("Please enter a name");
      return;
    }
    if (next === (user?.name || "")) return;
    setSavingName(true);
    try {
      const { data } = await api.patch("/settings", { name: next });
      setUser({ ...user, ...data });
      toast.success("Name updated");
    } catch (e) {
      toast.error("Could not update name", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSavingName(false);
    }
  };

  const enableNotifs = async () => {
    const res = await requestNotificationPermission();
    setNotifPerm(res);
    if (res === "granted") toast.success("Notifications enabled");
    else if (res === "denied") toast.error("Notifications blocked in browser settings");
    else if (res === "unsupported") toast.error("Notifications not supported on this device");
  };

  const signOutEverywhere = async () => {
    if (!window.confirm("Sign out of Lumivera on every device? You'll need to log in again everywhere.")) return;
    try {
      await api.post("/auth/revoke-all-sessions");
      setAuthToken(null);
      setUser(false);
      toast.success("Signed out everywhere.");
      navigate("/login", { replace: true });
    } catch (e) {
      toast.error("Could not sign out everywhere", { description: e?.response?.data?.detail || e.message });
    }
  };

  const deleteAccount = async () => {
    if (deleteConfirmEmail.trim().toLowerCase() !== (user?.email || "").toLowerCase()) {
      toast.error("Email confirmation does not match");
      return;
    }
    setDeleting(true);
    try {
      await api.delete("/auth/me", { data: { confirm_email: deleteConfirmEmail.trim() } });
      setAuthToken(null);
      setUser(false);
      setDeleteOpen(false);
      toast.success("Your account and all data have been deleted.");
      navigate("/login", { replace: true });
    } catch (e) {
      toast.error("Could not delete account", { description: e?.response?.data?.detail || e.message });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <div className="max-w-3xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <MenuSheet />
          <Button
            variant="ghost"
            size="sm"
            asChild
            className="rounded-xl"
            data-testid="settings-back-button"
          >
            <Link to="/">
              <ArrowLeft strokeWidth={1.5} className="size-4 mr-1.5" />
              Back to planner
            </Link>
          </Button>
        </div>

        {/* Title */}
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            <SettingsIcon strokeWidth={1.5} className="size-3.5" />
            Settings
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">
            Your account & preferences
          </h1>
        </div>

        {/* Account */}
        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle className="text-lg font-display">Account</CardTitle>
            <CardDescription>Your signed-in identity. Your name appears in your password reset and welcome emails.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="rounded-xl border border-border p-3 space-y-2">
              <div className="flex items-center gap-2">
                <div className="size-10 rounded-xl bg-primary/15 flex items-center justify-center shrink-0">
                  <User strokeWidth={1.5} className="size-5 text-primary" />
                </div>
                <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
                  Name
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  value={profileName}
                  onChange={(e) => setProfileName(e.target.value)}
                  className="rounded-xl"
                  placeholder="Your name"
                  maxLength={100}
                  data-testid="settings-name-input"
                />
                <Button
                  onClick={saveProfileName}
                  disabled={savingName || !profileName.trim() || profileName.trim() === (user?.name || "")}
                  className="rounded-xl shrink-0"
                  data-testid="settings-name-save"
                >
                  {savingName ? "Saving…" : "Save"}
                </Button>
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-xl border border-border p-3">
              <div className="size-10 rounded-xl bg-primary/15 flex items-center justify-center">
                <Mail strokeWidth={1.5} className="size-5 text-primary" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
                  Email
                </div>
                <div className="font-semibold truncate" data-testid="settings-email">
                  {user?.email}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Notifications */}
        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle className="text-lg font-display flex items-center gap-2">
              {notifPerm === "granted" ? (
                <Bell strokeWidth={1.5} className="size-5 text-primary" />
              ) : (
                <BellOff strokeWidth={1.5} className="size-5 text-muted-foreground" />
              )}
              Notifications
            </CardTitle>
            <CardDescription>
              Used for event reminders. Works on your phone when this app is installed to your home screen.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-sm">
                Status:{" "}
                <span
                  className="font-semibold"
                  data-testid="settings-notif-status"
                >
                  {notifPerm === "granted"
                    ? "Enabled"
                    : notifPerm === "denied"
                    ? "Blocked in browser settings"
                    : notifPerm === "unsupported"
                    ? "Not supported on this device"
                    : "Not yet enabled"}
                </span>
              </div>
              <Button
                onClick={enableNotifs}
                disabled={notifPerm === "granted" || notifPerm === "denied" || notifPerm === "unsupported"}
                className="rounded-xl"
                data-testid="settings-enable-notifs"
              >
                {notifPerm === "granted" ? "Enabled" : "Enable notifications"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Notifications shortcut — points to the full notifications page. */}
        <Card
          className="rounded-2xl cursor-pointer hover:shadow-md transition"
          data-testid="notifications-shortcut"
        >
          <Link to="/settings/notifications" className="block">
            <CardContent className="pt-5 flex items-center gap-3">
              <div className="size-11 rounded-2xl bg-primary/15 flex items-center justify-center shrink-0">
                <Bell strokeWidth={1.5} className="size-5 text-primary" />
              </div>
              <div className="flex-1">
                <div className="font-semibold">Notifications & reminders</div>
                <div className="text-xs text-muted-foreground">
                  Choose which reminders Lumivera sends, and when.
                </div>
              </div>
              <ArrowRight strokeWidth={1.5} className="size-4 text-muted-foreground" />
            </CardContent>
          </Link>
        </Card>

        {/* Daily digest email */}
        <Card className="rounded-2xl" data-testid="digest-card">
          <CardHeader>
            <CardTitle className="text-lg font-display flex items-center gap-2">
              <Sunrise strokeWidth={1.5} className="size-5 text-primary" />
              Daily digest email
            </CardTitle>
            <CardDescription>
              A gentle morning email with today's tasks, events and your daily focus. Sent at the time you choose, in your local timezone.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-sm">
                Status:{" "}
                <span className="font-semibold" data-testid="digest-status">
                  {digestEnabled ? "Sending daily" : "Off"}
                </span>
              </div>
              <Button
                onClick={() => toggleDigest(!digestEnabled)}
                disabled={savingDigest}
                variant={digestEnabled ? "outline" : "default"}
                className="rounded-xl"
                data-testid="digest-toggle"
              >
                {digestEnabled ? "Turn off" : "Turn on"}
              </Button>
            </div>
            {digestEnabled && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="digest-prefs">
                <div className="space-y-1.5">
                  <label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground flex items-center gap-1">
                    <Clock strokeWidth={1.5} className="size-3.5" />
                    Send at
                  </label>
                  <Input
                    type="time"
                    value={digestTime}
                    onChange={(e) => setDigestTime(e.target.value)}
                    onBlur={() =>
                      digestTime !== (user?.digest_time || "08:00") &&
                      saveDigest({ digest_time: digestTime, digest_timezone: digestTz })
                    }
                    className="rounded-xl"
                    data-testid="digest-time-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
                    Timezone
                  </label>
                  <Input
                    value={digestTz}
                    onChange={(e) => setDigestTz(e.target.value)}
                    onBlur={() =>
                      digestTz !== (user?.digest_timezone || browserTz) &&
                      saveDigest({ digest_time: digestTime, digest_timezone: digestTz })
                    }
                    placeholder={browserTz}
                    className="rounded-xl font-mono text-sm"
                    data-testid="digest-tz-input"
                  />
                  <p className="text-[10px] text-muted-foreground">
                    e.g. {browserTz}
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Appearance shortcut */}
        <Link
          to="/theme"
          className="block rounded-2xl border-2 border-border bg-card p-5 hover:border-primary/50 hover:-translate-y-[1px] transition-all shadow-sm"
          data-testid="settings-theme-link"
        >
          <div className="flex items-center gap-4">
            <div className="size-11 rounded-xl bg-primary/15 flex items-center justify-center shrink-0">
              <Palette strokeWidth={1.5} className="size-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display text-lg font-bold tracking-tight">Appearance</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Light/dark mode, palettes, custom colors and background patterns.
              </div>
            </div>
            <ArrowRight strokeWidth={1.5} className="size-5 text-muted-foreground shrink-0" />
          </div>
        </Link>

        {/* Sign out everywhere */}
        <Card className="rounded-2xl" data-testid="sessions-card">
          <CardHeader>
            <CardTitle className="text-lg font-display flex items-center gap-2">
              <ShieldOff strokeWidth={1.5} className="size-5 text-primary" />
              Active sessions
            </CardTitle>
            <CardDescription>
              Signed in on a device you no longer have access to? Sign out of every device at once.
              Useful if you suspect your account was used by someone else.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              variant="outline"
              className="rounded-xl"
              onClick={signOutEverywhere}
              data-testid="settings-sign-out-everywhere"
            >
              <ShieldOff strokeWidth={1.5} className="size-4 mr-1.5" />
              Sign out of all devices
            </Button>
          </CardContent>
        </Card>

        {/* Danger zone */}
        <Card className="rounded-2xl border-2 border-destructive/30" data-testid="danger-zone">
          <CardHeader>
            <CardTitle className="text-lg font-display flex items-center gap-2 text-destructive">
              <AlertTriangle strokeWidth={1.5} className="size-5" />
              Danger zone
            </CardTitle>
            <CardDescription>
              Permanently delete your Lumivera account and every piece of data tied to it.
              This action cannot be undone.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Dialog open={deleteOpen} onOpenChange={(o) => { setDeleteOpen(o); if (!o) setDeleteConfirmEmail(""); }}>
              <DialogTrigger asChild>
                <Button
                  variant="destructive"
                  className="rounded-xl"
                  data-testid="settings-delete-account-trigger"
                >
                  <Trash2 strokeWidth={1.5} className="size-4 mr-1.5" />
                  Delete my account
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-2xl" data-testid="delete-account-dialog">
                <DialogHeader>
                  <DialogTitle className="font-display flex items-center gap-2 text-destructive">
                    <AlertTriangle strokeWidth={1.5} className="size-5" />
                    Delete account permanently?
                  </DialogTitle>
                  <DialogDescription>
                    This will erase your profile, events, tasks, goals, journal entries, letters, vision boards, reviews,
                    Sunday Resets, and notification tokens — instantly and forever.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <p className="text-sm">
                    To confirm, type your email <span className="font-semibold">{user?.email}</span> below:
                  </p>
                  <Input
                    type="email"
                    value={deleteConfirmEmail}
                    onChange={(e) => setDeleteConfirmEmail(e.target.value)}
                    placeholder={user?.email}
                    className="rounded-xl"
                    data-testid="delete-account-email-input"
                  />
                </div>
                <DialogFooter className="gap-2 sm:gap-2">
                  <Button
                    variant="ghost"
                    onClick={() => setDeleteOpen(false)}
                    disabled={deleting}
                    className="rounded-xl"
                    data-testid="delete-account-cancel"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={deleteAccount}
                    disabled={
                      deleting ||
                      deleteConfirmEmail.trim().toLowerCase() !== (user?.email || "").toLowerCase()
                    }
                    className="rounded-xl"
                    data-testid="delete-account-confirm"
                  >
                    <Trash2 strokeWidth={1.5} className="size-4 mr-1.5" />
                    {deleting ? "Deleting…" : "Yes, delete everything"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
